import ClearIcon from "@mui/icons-material/Clear";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { motion } from "framer-motion";
import {
	memo,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../../components/CustomButton/CustomButton.js";
import FiltroV2 from "../../../components/FiltroV2/FiltroV2.js";
import ItemNotFound from "../../../components/ItemNotFound/ItemNotFound.tsx";
import LoadingComponent from "../../../components/LoadingComponent/LoadingComponent.tsx";
import NavbarV3 from "../../../components/NavbarV3/NavbarV3.tsx";
import ToastErro from "../../../components/Toast/ToastErro/ToastErro.tsx";
import MultiplosFiltrosRedux from "../../../contexts/MultiplosFiltros.js";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import RastrearRedux from "../../../contexts/Rastreio.js";
import MotionSemAutorizacao from "../../../core/components/MotionSemAutorizacao.tsx";
import Layout from "../../../core/layout/Layout.tsx";
import caminhaoPinCarregando from "../../../images/cam-greem-removebg-preview.png";
import caminhaoPin from "../../../images/caminhao_pin.png";
import { getLocalizadorVeiculos } from "../../../services/ModuloGerenciador/Search.js";
import "./LocalizadorVeiculos.css";

const itensFiltro = [
	{
		nomeColuna: "Placa Cavalo",
		field: "placaCavalo",
		filtro: "true",
	},
	{
		nomeColuna: "Placa Carreta",
		field: "placaCarreta",
		filtro: "true",
	},
	{
		nomeColuna: "Placa Segunda Carreta",
		field: "placaCarreta2",
		filtro: "true",
	},
	{
		nomeColuna: "Proprietario",
		field: "proprietario",
	},
	{
		nomeColuna: "Motorista",
		field: "veiculoMotorista",
	},
	{
		nomeColuna: "Motorista2",
		field: "motorista2",
	},
];

const RastreioCard = memo(({ veiculoSelecionado, onClose, navigate }) => {
	const { handleSetContextoEmbarque } = useProfile();

	const navegarEdicaoEmbarque = useCallback((veiculo) => {
		const contexto = veiculo.embarque.contexto
			? veiculo.embarque.contexto
			: "frota";

		handleSetContextoEmbarque(contexto);
		navigate(`/viagens/${contexto}/cadastro/${veiculo.embarque.idEmbarque}`);
	}, []);

	const formatarData = useCallback((data) => {
		const dataFormatada = new Date(data);
		const dataReferencia = new Date("2000-01-01T00:00:00");

		if (dataFormatada.getTime() === dataReferencia.getTime()) {
			return "Ainda não informado";
		}

		return dataFormatada.toLocaleDateString("pt-BR", {
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	}, []);

	return (
		<div className="rastreio-card">
			<div className="card-header-veiculos">
				<h2>Dados dos Veículos</h2>
				<ClearIcon className="close-button" onClick={onClose} />
			</div>
			<div className="card-header-veiculos">
				<h4>Clique no card para ser direcionado para a Viagem!</h4>
			</div>
			<div
				style={{
					overflow: "auto",
					gap: "12px",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{veiculoSelecionado.localizacao.map((veiculo, index) => (
					<div
						key={index}
						className="veiculo-detalhes"
						onClick={() => {
							navegarEdicaoEmbarque(veiculo);
						}}
					>
						<p>
							<strong>
								Veículo: <strong>{index + 1}</strong>
							</strong>
						</p>
						<p>
							<strong>Motorista:</strong> {veiculo.motorista}
						</p>
						<p>
							<strong>Placa Veículo:</strong> {veiculo.placaVeiculo}
						</p>
						<p>
							<strong>Previsão Chegada:</strong>{" "}
							{formatarData(veiculo.previsaoChegada)}
						</p>
						<p>
							<strong>Proprietário:</strong> {veiculo.proprietario}
						</p>
						<p>
							<strong>Saída:</strong> {formatarData(veiculo.saida)}
						</p>
					</div>
				))}
			</div>
		</div>
	);
});

const EventosCard = memo(({ dadosEventos }) => {
	if (dadosEventos.length > 0) {
		return (
			<div className="eventos-card">
				<h2>Eventos</h2>
				{dadosEventos.map((dado, index) => (
					<p key={index}>
						<strong>-</strong> {dado.evento.nome}
					</p>
				))}
			</div>
		);
	}
});

function LocalizadorVeiculos(props) {
	const navigate = useNavigate();

	const voltar = () => {
		navigate(-1);
	};

	const { dadosRastrearRedux, setDadosRastrearRedux } =
		useContext(RastrearRedux);

	const { profile, hasRegra } = useProfile();

	const [loading, setLoading] = useState(true);

	const [semAutorizacao, setSemAutorizacao] = useState(false);

	const [openToastErro, setOpenToastErro] = useState(false);

	const [dadosEventos] = useState([]);

	const [lat, setLat] = useState(null);

	const [lng, setLng] = useState(null);

	const [veiculoSelecionado, setVeiculoSelecionado] = useState(null);

	const [
		localizadoresVeiculosDescarregando,
		setLocalizadoresVeiculosDescarregando,
	] = useState([]);

	const [localizadoresVeiculosCarregando, setLocalizadoresVeiculosCarregando] =
		useState([]);

	const { isLoaded, loadError } = useLoadScript({
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
	});

	const [isMapLoaded, setIsMapLoaded] = useState(false);

	const [polygon, setPolygon] = useState({ paths: [] });

	const mapRef = useRef(null);

	const handleMapLoaded = (map) => {
		mapRef.current = map;
		setIsMapLoaded(true);
	};

	useEffect(() => {
		const applyCustomStyles = () => {
			const cameraControl = document.querySelector(
				"gmp-internal-camera-control"
			);
			if (cameraControl) {
				const parentElement = cameraControl.parentElement;
				if (parentElement) {
					parentElement.style.right = "100px";
				}
				const menuElement = cameraControl.querySelector("menu");
				if (menuElement) {
					menuElement.style.width = "315px";
					menuElement.style.height = "175px";
					menuElement.style.top = "-80px";
				}
			}
		};

		const handleFullscreen = () => {
			setTimeout(() => {
				applyCustomStyles();
			}, 500);
		};

		const monitorFullscreen = () => {
			const fullscreenButton = document.querySelector(".gm-fullscreen-control");
			if (fullscreenButton) {
				fullscreenButton.addEventListener("click", handleFullscreen);
				document.addEventListener("keydown", (event) => {
					if (event.key === "Escape") {
						handleFullscreen();
					}
				});
			}
		};

		let observer;

		if (isMapLoaded) {
			applyCustomStyles();
			monitorFullscreen();

			observer = new MutationObserver(() => {
				applyCustomStyles();
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			});
		}

		document.addEventListener("fullscreenchange", handleFullscreen);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreen);
			const fullscreenButton = document.querySelector(".gm-fullscreen-control");
			if (fullscreenButton) {
				fullscreenButton.removeEventListener("click", handleFullscreen);
			}
			if (observer) {
				observer.disconnect();
			}
		};
	}, [isMapLoaded]);

	const [drawnPolygon, setDrawnPolygon] = useState(null);

	useEffect(() => {
		if (isMapLoaded && polygon.paths.length > 0) {
			setTimeout(() => {
				if (drawnPolygon) {
					drawnPolygon.setMap(null);
				}

				const newPolygon = new window.google.maps.Polygon({
					paths: polygon.paths,
					strokeColor: "#FF0000",
					strokeOpacity: 0.8,
					strokeWeight: 2,
					fillColor: "#FF0000",
					fillOpacity: 0.35,
				});

				newPolygon.setMap(mapRef.current);
				setDrawnPolygon(newPolygon);
			}, 200);
		}
	}, [polygon, isMapLoaded]);

	const handleCloseToastErro = (value) => {
		setOpenToastErro(false);
	};

	const [textoErro, setTextoErro] = useState("");

	const [valuesPesquisa, setValuesPesquisa] = useState([]);

	const buscarVeiculosInicial = async () => {
		setLoading(true);
		setValuesPesquisa((current) => ({
			...current,
			embarqueInicio: formatarData2(new Date()),
			embarqueFinal: formatarData2(new Date()),
		}));
		await getLocalizadorVeiculos(
			profile?.token,
			3,
			"todos",
			formatarData2(new Date()),
			formatarData2(new Date())
		)
			.then((result) => {
				setVeiculoSelecionado(null);
				setLocalizadoresVeiculosDescarregando(result.data.descarregando);
				setLocalizadoresVeiculosCarregando(result.data.carregando);
			})
			.catch((error) => {
				if (error.response.data.length > 200) {
					setTextoErro(
						"Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte."
					);
				} else {
					setTextoErro(error.response.data);
				}
				setOpenToastErro(true);
			});
		setLoading(false);
	};

	const { multiplosFiltrosRedux } = useContext(MultiplosFiltrosRedux);

	const buscarVeiculosSearch = async () => {
		setLoading(true);
		let pesquisaFormatada = `${valuesPesquisa.item}=${
			valuesPesquisa.pesquisa ?? ""
		}`;
		if (multiplosFiltrosRedux && multiplosFiltrosRedux.length > 0) {
			for (let filtro of multiplosFiltrosRedux) {
				pesquisaFormatada += `&${filtro.field}=${filtro.valor}`;
			}
		}
		await getLocalizadorVeiculos(
			profile?.token,
			valuesPesquisa.tipo ?? 3,
			valuesPesquisa.contexto ?? "todos",
			valuesPesquisa.embarqueInicio,
			valuesPesquisa.embarqueFinal,
			pesquisaFormatada
		)
			.then((result) => {
				setLocalizadoresVeiculosDescarregando(result.data.descarregando);
				setLocalizadoresVeiculosCarregando(result.data.carregando);
				setVeiculoSelecionado(null);
			})
			.catch((error) => {
				console.error(error);

				if (error.response?.data?.length > 200) {
					setTextoErro(
						"Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte."
					);
				} else {
					setTextoErro(error.response.data);
				}
				setOpenToastErro(true);
			});
		setLoading(false);
	};

	useEffect(() => {
		if (!!valuesPesquisa.tipo) {
			buscarVeiculosSearch();
		}
	}, [valuesPesquisa.tipo]);

	useEffect(() => {
		if (!!lat && !!lng && dadosEventos.length > 0) {
			setLoading(false);
		}
	}, [lat, lng, dadosEventos.length]);

	const formatarData2 = (dataString) => {
		const data = new Date(dataString);
		const dia = data.getDate().toString().padStart(2, "0");
		const mes = (data.getMonth() + 1).toString().padStart(2, "0");
		const ano = data.getFullYear();
		return `${ano}-${mes}-${dia}`;
	};

	const [mapCenter, setMapCenter] = useState({
		lat: -13.352436164089657,
		lng: -49.59815951547697,
	});

	const handleMarkerClick = (veiculo) => {
		setVeiculoSelecionado(veiculo);
		setMapCenter({ lat: veiculo.latitude, lng: veiculo.longitude });
	};

	const limpar = () => {
		buscarVeiculosInicial();
	};

	useEffect(() => {
		if (profile?.token) {
			setSemAutorizacao(!hasRegra("Localizador Veículos"));
			buscarVeiculosInicial();
		}
	}, [profile?.token]);

	if (semAutorizacao) {
		return <MotionSemAutorizacao />;
	}

	if (!isLoaded) {
		return (
			<Layout>
				<NavbarV3 selecionado="Localizador Veículos" />
				<LoadingComponent />
			</Layout>
		);
	}

	if (loadError) {
		return (
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
			>
				<NavbarV3 selecionado="Localizador Veículos" />
				<div className="principal-mapa">
					<ItemNotFound label="Erro ao carregar o mapa" />
				</div>
			</motion.div>
		);
	}

	return (
		<Layout>
			{props.navbar || <NavbarV3 selecionado="Localizador Veículos" />}
			<div className="principal-mapa">
				<div className="voltar-card-veiculos">
					<FiltroV2
						classe="div-filtrar-embarque-v2"
						classOption="select-options-cliente"
						itensFiltro={itensFiltro}
						valuesPesquisa={valuesPesquisa}
						setValuesPesquisa={setValuesPesquisa}
						buscarSearch={buscarVeiculosSearch}
						limpar={limpar}
					/>
					<CustomButton
						classCss="custom-button-voltar"
						titulo="Voltar"
						cor="#0946B8"
						onClick={() => {
							voltar();
							setDadosRastrearRedux({});
						}}
					/>
				</div>
				<div className="legenda-card-veiculos">
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<img style={{ width: "30px" }} src={caminhaoPinCarregando} />
						<span>Carregando</span>
					</div>
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<img style={{ width: "30px" }} src={caminhaoPin} />
						<span>Descarregando</span>
					</div>
				</div>
				{dadosRastrearRedux.endereco ? (
					<>{!loading && <EventosCard dadosEventos={dadosEventos} />}</>
				) : (
					veiculoSelecionado && (
						<RastreioCard
							veiculoSelecionado={veiculoSelecionado}
							navigate={navigate}
							onClose={() => setVeiculoSelecionado(null)}
						/>
					)
				)}
				<GoogleMap
					useLoadScript
					mapContainerStyle={{
						width: "100%",
						height: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
					center={mapCenter}
					zoom={5}
					onLoad={handleMapLoaded}
				>
					{isMapLoaded &&
						localizadoresVeiculosDescarregando !== null &&
						localizadoresVeiculosDescarregando.length > 0 &&
						localizadoresVeiculosDescarregando.map((localizador, index) => (
							<Marker
								key={index}
								position={{
									lat: localizador.latitude + 0.002,
									lng: localizador.longitude + 0.002,
								}}
								icon={{
									url: caminhaoPin,
									scaledSize: new window.google.maps.Size(50, 50),
									labelOrigin: new window.google.maps.Point(25, 1),
								}}
								label={{
									text: `${localizador.numeroVeiculos}`,
									color: "black",
									fontSize: "18px",
									fontWeight: "bold",
								}}
								onClick={() => handleMarkerClick(localizador)}
							/>
						))}
					{isMapLoaded &&
						localizadoresVeiculosCarregando !== null &&
						localizadoresVeiculosCarregando.length > 0 &&
						localizadoresVeiculosCarregando.map((localizador, index) => (
							<Marker
								key={index}
								position={{
									lat: localizador.latitude,
									lng: localizador.longitude,
								}}
								icon={{
									url: caminhaoPinCarregando,
									scaledSize: new window.google.maps.Size(50, 50),
									labelOrigin: new window.google.maps.Point(25, 1),
								}}
								label={{
									text: `${localizador.numeroVeiculos}`,
									color: "black",
									fontSize: "18px",
									fontWeight: "bold",
								}}
								onClick={() => handleMarkerClick(localizador)}
							/>
						))}
				</GoogleMap>
			</div>
			<ToastErro
				texto={textoErro}
				open={openToastErro}
				onClose={handleCloseToastErro}
			/>
		</Layout>
	);
}

export default LocalizadorVeiculos;
